import { finishProfiles, IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen, Panel, PanelItem } from '@rta/rta-blueprint-library';

import { getIslandSuffix } from './get-island-suffix';
import { getOverhangReductionCauses } from './get-overhang-reduction-causes';
import { hasLevelingLegs } from './has-leveling-legs';
import { isJointCountertop } from './is-joint-countertop';
import type { BlueprintPage } from '../types';

export type NonStandardChange = {
	pageRef: string;
	description: string;
	sortKey: number;
	// Number of distinct changes this row represents (e.g. 2 sides, 3x cutouts) — summed for the "Qty-N" total.
	count: number;
};

export type GetNonStandardChangesParams = {
	kitchen: Kitchen;
	pages: Array<BlueprintPage>;
};

const STANDARD_OVERHANG = 2;
const STANDARD_SEATING_OVERHANG = 10;
const MOKS_STANDARD_OVERHANG = 0.75;
const MOKS_SEATING_OVERHANG = 8;
const DEFAULT_BASE_HEIGHT = 34.5;
const DEFAULT_BASE_DEPTH = 28;
const BAR_STANDARD_HEIGHT = 39.25;
const CORNER_STANDARD_HEIGHT = 37;

// MOKS is signalled by the countertop material key OR the surround key (island override, else kitchen default) starting with 'mok'.
const isMoksCountertop = (island: Island, kitchen: Kitchen): boolean => {
	const countertopKey = island.getCountertop()?.material?.key ?? '';
	const surroundKey = island.surround?.key ?? kitchen.material?.surround?.key ?? '';
	return countertopKey.toLowerCase().startsWith('mok') || surroundKey.toLowerCase().startsWith('mok');
};

/* -------------------------------------------------------------------------- */
/*  Page lookup helpers                                                       */
/* -------------------------------------------------------------------------- */

// Page references are answered by the content metadata each page carries (the
// actual Panel / Island / Countertop objects it draws) — never by pageType inference.
const getPageForPanel = (pages: Array<BlueprintPage>, panel: Panel): number | null => {
	return pages.find((p) => p.content?.panels.some((drawn) => drawn.id === panel.id))?.pageNumber ?? null;
};

const getCountertopPageNumber = (pages: Array<BlueprintPage>, islandId: string): number | null => {
	return pages.find((p) => p.content?.countertops.some((ct) => ct.islandIds.includes(islandId)))?.pageNumber ?? null;
};

const getPanelDirection = (panel: Panel): string => {
	if (panel.isFrontPanel) return 'front';
	if (panel.isBackPanel) return 'back';
	if (panel.isLeftPanel) return 'left side';
	if (panel.isRightPanel) return 'right side';
	return 'side';
};

const getIslandLabel = (island: Island, kitchen: Kitchen): string => {
	const islandsCount = kitchen.design.islands.children.length;
	if (islandsCount <= 1) return '';
	const letter = getIslandSuffix({ island, kitchen });
	return letter ? ` Island ${letter}` : '';
};

const getIslandRangePages = (pages: Array<BlueprintPage>, islandId: string): Array<number> => {
	return pages
		.filter((p) => p.content?.islands.some((i) => i.id === islandId))
		.map((p) => p.pageNumber)
		.sort((a, b) => a - b);
};

/* -------------------------------------------------------------------------- */
/*  Quantity formatter                                                        */
/* -------------------------------------------------------------------------- */

const formatQuantityPrefix = (count: number, noun: string): string => {
	if (count <= 1) return noun;
	return `${count}x ${noun}s`;
};

/* -------------------------------------------------------------------------- */
/*  SKU / category classification                                             */
/* -------------------------------------------------------------------------- */

const isGasLineSku = (panelItem: PanelItem): boolean => {
	const sku = (panelItem.item?.sku ?? '').toUpperCase();
	const label = (panelItem.item?.label ?? '').toUpperCase();
	return sku.includes('GAS_LINE') || label.includes('GAS_LINE') || label.includes('GAS LINE');
};

const isUmbrellaSku = (panelItem: PanelItem): boolean => {
	const sku = (panelItem.item?.sku ?? '').toUpperCase();
	const label = (panelItem.item?.label ?? '').toUpperCase();
	return sku.includes('UMBRELLA') || label.includes('UMBRELLA');
};

// The catalog sku is WOOD; the legacy raw importer emits LOG-INSERT for the same opening (process-raw-blueprint.class.ts).
const isWoodStorageSku = (panelItem: PanelItem): boolean => {
	const sku = (panelItem.item?.sku ?? '').toUpperCase();
	return sku === 'WOOD' || sku === 'LOG-INSERT';
};

const isPopUpOutletSku = (panelItem: PanelItem): boolean => {
	const sku = (panelItem.item?.sku ?? '').toUpperCase();
	const label = (panelItem.item?.label ?? '').toUpperCase();
	const displayName = (panelItem.item?.displayName ?? '').toUpperCase();
	return label.includes('POP-UP') || label.includes('POPUP') || displayName.includes('POP-UP') || sku.includes('POP-UP');
};

// Catalog has no componentKey on the domain model and the sku is not unique (DOUBLE-GANG-OUTLET and POP-UP-OUTLET share LEF-OB-2-CT-SP), so match the way isPopUpOutletSku does.
const getOutletGang = (panelItem: PanelItem): 'Single Gang ' | 'Double Gang ' | '' => {
	const sku = (panelItem.item?.sku ?? '').toUpperCase();
	const label = (panelItem.item?.label ?? '').toUpperCase();
	const displayName = (panelItem.item?.displayName ?? '').toUpperCase();
	const text = `${sku} ${label} ${displayName}`;

	if (text.includes('DOUBLE') || text.includes('OUTLET-DBL')) return 'Double Gang ';
	if (text.includes('SINGLE') || text.includes('OUTLET-S-')) return 'Single Gang ';

	return '';
};

const isTopPlaced = (panelItem: PanelItem): boolean => {
	return (panelItem.item?.placement ?? '').toLowerCase().includes('top');
};

const isOutletCategory = (panelItem: PanelItem): boolean => {
	return panelItem.item?.category?.key === 'outlet';
};

const isHoleCategory = (panelItem: PanelItem): boolean => {
	return panelItem.item?.category?.key === 'hole';
};

const isCutoutCategory = (panelItem: PanelItem): boolean => {
	return panelItem.item?.category?.key === 'cutout';
};

/* -------------------------------------------------------------------------- */
/*  Per-panel item counters                                                   */
/* -------------------------------------------------------------------------- */

const countMatching = (panel: Panel, predicate: (pi: PanelItem) => boolean): number => {
	let count = 0;
	panel.panelItemGroups.children.forEach((group) => {
		group.items.children.forEach((panelItem: PanelItem) => {
			if (predicate(panelItem)) count++;
		});
	});
	return count;
};

/* -------------------------------------------------------------------------- */
/*  Detector: outlet cutouts on non-countertop panels                         */
/* -------------------------------------------------------------------------- */

const detectOutletCutouts = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);
		island.panels.children.forEach((panel) => {
			const isPanelOutlet = (pi: PanelItem): boolean => isOutletCategory(pi) && !isTopPlaced(pi) && !isPopUpOutletSku(pi);
			if (countMatching(panel, isPanelOutlet) === 0) return;
			const pageNumber = getPageForPanel(pages, panel);
			if (pageNumber === null) return;
			const direction = getPanelDirection(panel);

			// MFG asked for the gang size on the note; an unrecognised outlet keeps the generic wording rather than guessing
			(['Double Gang ', 'Single Gang ', ''] as const).forEach((gang) => {
				const count = countMatching(panel, (pi) => isPanelOutlet(pi) && getOutletGang(pi) === gang);
				if (count === 0) return;
				const noun = formatQuantityPrefix(count, `${gang}Outlet ${gang === '' ? 'cutout' : 'Cutout'}`);
				changes.push({
					pageRef: `Pg. ${pageNumber}`,
					description: `${noun} on${islandLabel} ${direction} panel`,
					sortKey: pageNumber,
					count
				});
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: pop-up outlets on countertop                                    */
/* -------------------------------------------------------------------------- */

const detectPopUpOutlets = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);
		const ctPage = getCountertopPageNumber(pages, island.id);
		if (ctPage === null) return;

		let count = 0;
		island.panels.children.forEach((panel) => {
			count += countMatching(panel, (pi) => isPopUpOutletSku(pi) || (isOutletCategory(pi) && isTopPlaced(pi)));
		});
		if (count === 0) return;

		const noun = formatQuantityPrefix(count, 'pop-up outlet');
		const surface = isJointCountertop({ island, kitchen }) ? 'Joint Countertop' : `${islandLabel.trim() || 'countertop'} countertop`.replace(/^\s*countertop countertop/, 'countertop');
		changes.push({
			pageRef: `Pg. ${ctPage}`,
			description: islandLabel ? `${noun} on${islandLabel} countertop` : `${noun} on countertop`,
			sortKey: ctPage,
			count
		});
		// Silence unused-var lint for surface (kept for future joint labeling)
		void surface;
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: gas line cutouts (countertop or panel)                          */
/* -------------------------------------------------------------------------- */

const detectGasLineCutouts = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);

		// Countertop-placed gas line cutouts (top only)
		let topCount = 0;
		island.panels.children.forEach((panel) => {
			topCount += countMatching(panel, (pi) => isGasLineSku(pi) && isTopPlaced(pi));
		});
		if (topCount > 0) {
			const ctPage = getCountertopPageNumber(pages, island.id);
			if (ctPage !== null) {
				const noun = formatQuantityPrefix(topCount, 'Gas line cutout');
				changes.push({
					pageRef: `Pg. ${ctPage}`,
					description: `${noun} on${islandLabel} countertop`,
					sortKey: ctPage,
					count: topCount
				});
			}
		}

		// Panel-placed gas line cutouts (front/side/back)
		island.panels.children.forEach((panel) => {
			const count = countMatching(panel, (pi) => isGasLineSku(pi) && !isTopPlaced(pi));
			if (count === 0) return;
			const pageNumber = getPageForPanel(pages, panel);
			if (pageNumber === null) return;
			const noun = formatQuantityPrefix(count, 'Gas line cutout');
			const direction = getPanelDirection(panel);
			changes.push({
				pageRef: `Pg. ${pageNumber}`,
				description: `${noun} on${islandLabel} ${direction} panel`,
				sortKey: pageNumber,
				count
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: umbrella cutouts on countertop                                  */
/* -------------------------------------------------------------------------- */

const detectUmbrellaCutouts = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);
		const ctPage = getCountertopPageNumber(pages, island.id);
		if (ctPage === null) return;

		let count = 0;
		island.panels.children.forEach((panel) => {
			count += countMatching(panel, (pi) => isUmbrellaSku(pi));
		});
		if (count === 0) return;

		const noun = formatQuantityPrefix(count, 'Umbrella cutout');
		changes.push({
			pageRef: `Pg. ${ctPage}`,
			description: `${noun} on${islandLabel} countertop`,
			sortKey: ctPage,
			count
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: wood storage cutouts (log insert)                               */
/* -------------------------------------------------------------------------- */

// Wording is verbatim from production: "Pg. 5: Wood storage cutout on Island A front panel"
// (docs/blueprint-template-fingerprint.json). The log insert is a catalog Coyote sku in the
// cutout category, so it is neither a custom cutout nor a Kitchen Overview appliance row.
const detectWoodStorageCutouts = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);

		island.panels.children.forEach((panel) => {
			const count = countMatching(panel, (pi) => isWoodStorageSku(pi));
			if (count === 0) return;
			const pageNumber = getPageForPanel(pages, panel);
			if (pageNumber === null) return;
			const noun = formatQuantityPrefix(count, 'Wood storage cutout');
			const direction = getPanelDirection(panel);
			changes.push({
				pageRef: `Pg. ${pageNumber}`,
				description: `${noun} on${islandLabel} ${direction} panel`,
				sortKey: pageNumber,
				count
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: generic custom cutouts (panel + countertop)                     */
/* -------------------------------------------------------------------------- */

const detectCustomCutouts = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	// A "custom cutout" on PDFs refers to an opening the designer drew by hand in the
	// `cutout` or `hole` category. Those are always override items (add-cutout-panel
	// builds a component with sku CUTOUT and override: true) — a non-override item
	// resolves to a real catalog sku, which cannot be custom no matter its category.
	// That is what keeps WOOD (Log Insert) and C124KEG-TAP (Kegerator Tap) out of here.
	// A cutout carrying a named appliance model in customLabel (kegerator, etc.) is a standard appliance opening, not a custom cutout — genuine custom holes leave customLabel blank.
	const isNamedAppliance = (pi: PanelItem): boolean => {
		const customLabel = (pi.item?.customLabel ?? '').trim().toUpperCase();
		return customLabel !== '' && customLabel !== 'CUTOUT' && customLabel !== 'CUSTOM';
	};
	// A small round countertop hole is a STANDARD umbrella (2.25" dia) or pass-through (2" dia) opening
	// per the MOKs playbook, not a custom cutout — so a top-placed round hole up to ~2.5" is not flagged.
	const isStandardRoundCountertopHole = (pi: PanelItem): boolean => {
		if (!isTopPlaced(pi)) return false;
		const { width, height } = pi.item?.cutout ?? { width: 0, height: 0 };
		if (!width || width !== height) return false;
		return width <= 2.5;
	};
	const isGenericCustomCutout = (pi: PanelItem): boolean => {
		if (!(pi.item?.override ?? false)) return false;
		if (isGasLineSku(pi) || isUmbrellaSku(pi)) return false;
		if (isNamedAppliance(pi)) return false;
		if (isStandardRoundCountertopHole(pi)) return false;
		if (isCutoutCategory(pi)) return true;
		if (isHoleCategory(pi)) return true;
		const sku = pi.item?.sku ?? '';
		return sku === 'CUSTOM' || sku === 'CUTOUT';
	};

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);

		// Top-placed → countertop
		let topCount = 0;
		island.panels.children.forEach((panel) => {
			topCount += countMatching(panel, (pi) => isGenericCustomCutout(pi) && isTopPlaced(pi));
		});
		if (topCount > 0) {
			const ctPage = getCountertopPageNumber(pages, island.id);
			if (ctPage !== null) {
				const noun = formatQuantityPrefix(topCount, 'Custom cutout');
				changes.push({
					pageRef: `Pg. ${ctPage}`,
					description: `${noun} on${islandLabel} countertop`,
					sortKey: ctPage,
					count: topCount
				});
			}
		}

		// Panel-placed
		island.panels.children.forEach((panel) => {
			const count = countMatching(panel, (pi) => isGenericCustomCutout(pi) && !isTopPlaced(pi));
			if (count === 0) return;
			const pageNumber = getPageForPanel(pages, panel);
			if (pageNumber === null) return;
			const noun = formatQuantityPrefix(count, 'Custom cutout');
			const direction = getPanelDirection(panel);
			changes.push({
				pageRef: `Pg. ${pageNumber}`,
				description: `${noun} on${islandLabel} ${direction} panel`,
				sortKey: pageNumber,
				count
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: reduced / non-standard overhangs                                */
/* -------------------------------------------------------------------------- */

const NEIGHBOR_HEIGHT_EPS = 0.01;

// Sides whose overhang removal is a STANDARD change: the side butts a TALLER-OR-EQUAL island,
// meaning THIS island's top was cut so it fits against the neighbour (the smoker stand slid
// between two grills). Butting a SHORTER island stays non-standard — the removal accommodates
// the appliance on the shorter stand — as do wall/obstruction removals (not part of the kitchen).
const getStandardRemovalSides = (island: Island, kitchen: Kitchen): Set<'front' | 'left' | 'back' | 'right'> => {
	const sides = new Set<'front' | 'left' | 'back' | 'right'>();
	const { islands } = kitchen.design;
	const ownHeight = island.size?.height ?? 0;
	const connections = [...island.connections.getSourceConnections(), ...island.connections.getTargetConnections()];
	connections.forEach((connection) => {
		const isSource = connection.sourceIsland?.id === island.id;
		const neighbor = isSource ? connection.targetIsland : connection.sourceIsland;
		const neighborIsland = neighbor ? islands.findById(neighbor.id) : null;
		if (!neighborIsland) return;
		if ((neighborIsland.size?.height ?? 0) < ownHeight - NEIGHBOR_HEIGHT_EPS) return;
		const panel: Panel | null = isSource ? connection.sourcePanel : connection.targetPanel;
		if (!panel) return;
		if (panel.isFrontPanel) sides.add('front');
		else if (panel.isBackPanel) sides.add('back');
		else if (panel.isLeftPanel) sides.add('left');
		else if (panel.isRightPanel) sides.add('right');
	});
	return sides;
};

const detectReducedOverhangs = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	// Non-RTA countertops (key 'none') are templated on-site, so custom overhangs are excluded from the NSC list.
	if ((kitchen.material?.countertop?.key ?? '') === 'none') return [];
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);
		const overhangs = island.getOverhangs();
		const ctPage = getCountertopPageNumber(pages, island.id);
		if (ctPage === null) return;

		const isMoks = isMoksCountertop(island, kitchen);
		const standardOverhang = isMoks ? MOKS_STANDARD_OVERHANG : STANDARD_OVERHANG;
		const seatingOverhang = isMoks ? MOKS_SEATING_OVERHANG : STANDARD_SEATING_OVERHANG;
		const standardRemovalSides = getStandardRemovalSides(island, kitchen);
		const reductionCauses = getOverhangReductionCauses(island);

		// "on Island A countertop" in multi-island kitchens; "on countertop" when there is no island label.
		const surface = `on${islandLabel} countertop`;

		// Corner islands have two back panels — measure each so both removals are listed
		// ("back left" / "back right"); other islands measure the single combined 'back'.
		const sideKeys: Array<{ label: 'front' | 'left' | 'back' | 'right' | 'back left' | 'back right'; overhangKey: keyof typeof overhangs }> =
			island.type === IslandType.Corner
				? [
					{ label: 'front', overhangKey: 'front' },
					{ label: 'left', overhangKey: 'left' },
					{ label: 'right', overhangKey: 'right' },
					{ label: 'back left', overhangKey: 'leftBack' },
					{ label: 'back right', overhangKey: 'rightBack' }
				]
				: [
					{ label: 'front', overhangKey: 'front' },
					{ label: 'left', overhangKey: 'left' },
					{ label: 'back', overhangKey: 'back' },
					{ label: 'right', overhangKey: 'right' }
				];

		// Below-standard sides read "No <sides> overhang" (singular, no values). Sides cut to fit
		// a taller/equal island are standard and skipped; wall/obstruction removals and sides
		// facing a shorter stand are listed. Enlarged sides (above standard) print their value.
		// A Bar back is measured against its seating standard (10"/8"), not the flat 2".
		// A wall/obstruction abutment is listed based on the CONNECTION, not the numeric overhang:
		// the countertop physically has no overhang against a wall even if the value on the panel
		// has since been edited back toward standard.
		const noOverhangSides: Array<string> = [];
		const valueSides: Array<{ label: string; value: number }> = [];
		sideKeys.forEach(({ label, overhangKey }) => {
			const value = overhangs[overhangKey];
			if (value === undefined || value === null) return;
			const removalSide = label === 'back left' || label === 'back right' ? 'back' : label;
			const isBarBack = island.type === IslandType.Bar && label === 'back';
			const sideStandard = isBarBack ? seatingOverhang : standardOverhang;
			const abutsObstruction = reductionCauses.get(label) === 'obstruction';
			if (abutsObstruction && !isBarBack) {
				noOverhangSides.push(label);
				return;
			}
			if (value === sideStandard) return;
			// A Bar back reduced BELOW the non-seating standard (0 / OVERHANG_REMOVAL_BY_MATERIAL)
			// means the countertop no longer overhangs the back and reads as "No back overhang".
			// A back cut down to exactly the non-seating standard still overhangs — 2" of concrete
			// past the panel is 2" of overhang, not none — so it prints its value like any other
			// reduced-but-present overhang (#530). Mirrors the regular-side rule below.
			if (isBarBack && value < standardOverhang) {
				if (standardRemovalSides.has(removalSide)) return;
				noOverhangSides.push(label);
				return;
			}
			if (isBarBack) {
				valueSides.push({ label, value });
				return;
			}
			if (value < standardOverhang) {
				if (standardRemovalSides.has(removalSide)) return;
				noOverhangSides.push(label);
			}
			else valueSides.push({ label, value });
		});

		const joinSides = (sides: Array<string>): string => {
			if (sides.length === 1) return sides[0];
			if (sides.length === 2) return `${sides[0]} and ${sides[1]}`;
			return `${sides.slice(0, -1).join(', ')} and ${sides[sides.length - 1]}`;
		};

		if (noOverhangSides.length > 0) {
			changes.push({
				pageRef: `Pg. ${ctPage}`,
				description: `No ${joinSides(noOverhangSides)} overhang ${surface}`,
				sortKey: ctPage,
				count: noOverhangSides.length
			});
		}

		// Group sides that share the same value into one bullet (e.g. "10" back and right overhang").
		const sidesByValue = new Map<number, Array<string>>();
		valueSides.forEach(({ label, value }) => {
			sidesByValue.set(value, [...(sidesByValue.get(value) ?? []), label]);
		});
		sidesByValue.forEach((sides, value) => {
			changes.push({
				pageRef: `Pg. ${ctPage}`,
				description: `${value}" ${joinSides(sides)} overhang ${surface}`,
				sortKey: ctPage,
				count: sides.length
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: non-standard base height                                        */
/* -------------------------------------------------------------------------- */

const detectNonStandardBaseHeight = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		if (
			island.type !== IslandType.Grill
			&& island.type !== IslandType.Bar
			&& island.type !== IslandType.Smoker
			&& island.type !== IslandType.Corner
		) return;

		const height = island.size.height;
		// MOKS islands have a different standard base height than concrete/granite (SOP §9).
		const moksHeight = isMoksCountertop(island, kitchen) ? finishProfiles.mok.islandDimensions[island.type]?.height : undefined;
		if (moksHeight !== undefined && height === moksHeight) return;
		// Corner stands' standard base height is 37" (grill height on a Corner is itself the non-standard case).
		if (island.type === IslandType.Corner) {
			if (height === CORNER_STANDARD_HEIGHT) return;
		}
		else {
			if (height === DEFAULT_BASE_HEIGHT) return;
			if (island.type === IslandType.Bar && height === BAR_STANDARD_HEIGHT) return;
		}

		const islandLabel = getIslandLabel(island, kitchen);
		// The base height is drawn on the sheets that render panels; the countertop
		// sheet shows top measurements only, so it is not part of the range.
		const islandPages = pages
			.filter((p) => (p.content?.panels.length ?? 0) > 0 && p.content?.islands.some((i) => i.id === island.id))
			.map((p) => p.pageNumber)
			.sort((a, b) => a - b);
		if (islandPages.length === 0) return;
		const pageRef = islandPages.length === 1
			? `Pg. ${islandPages[0]}`
			: `Pgs. ${islandPages[0]}-${islandPages[islandPages.length - 1]}`;
		changes.push({
			pageRef,
			description: `${height}" base height on${islandLabel}`,
			sortKey: islandPages[0],
			count: 1
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: non-standard base depth                                         */
/* -------------------------------------------------------------------------- */

const detectNonStandardBaseDepth = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		if (island.type === IslandType.Corner) return;
		const rawDepth = island.size.depth;
		const depth: number = typeof rawDepth === 'object' && rawDepth !== null
			? ((rawDepth as { left: number; right: number }).left + (rawDepth as { left: number; right: number }).right) / 2
			: (rawDepth as number);
		if (!depth) return;
		// MOKS islands have a different standard base depth than concrete/granite (SOP §9).
		const moksDepth = isMoksCountertop(island, kitchen) ? finishProfiles.mok.islandDimensions[island.type]?.depth : undefined;
		if (moksDepth !== undefined && depth === moksDepth) return;
		if (depth === DEFAULT_BASE_DEPTH) return;

		const islandLabel = getIslandLabel(island, kitchen);
		// The base depth is the island's front-to-back dimension — it is drawn on the
		// side-panel sheet (the panel's own width axis IS the depth) and the countertop
		// sheet (the depth-axis dimension line), never on the front/back elevations.
		const refs = pages
			.filter((p) => p.content?.showsBaseDepth && p.content.islands.some((i) => i.id === island.id))
			.map((p) => p.pageNumber)
			.sort((a, b) => a - b);
		if (refs.length === 0) return;
		const pageRef = refs.length === 1 ? `Pg. ${refs[0]}` : `Pgs. ${refs.join(', ')}`;
		changes.push({
			pageRef,
			description: `${depth}" base depth on${islandLabel}`,
			sortKey: refs[0],
			count: 1
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: utility panels (back / left-side / right-side)                  */
/* -------------------------------------------------------------------------- */

const detectUtilityPanels = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const islandLabel = getIslandLabel(island, kitchen);
		const islandDesc = (island.description ?? '').split(' | ')[0] ?? '';

		island.panels.children.forEach((panel) => {
			// A panel is a utility panel either via its explicit flag or when it
			// carries a utility-cutout item. Neither the flag nor the utility-cutout
			// category is persisted reliably: `addUtilityPanelCutouts` never sets the
			// flag, and schema update 14 remaps legacy UTILITY-CUTOUT items to the
			// plain `cutout` category. The SKU is the stable marker — same predicate
			// as `clearUtilityCutouts` in add-utility-panel-cutouts.ts.
			const hasUtilityCutout = countMatching(panel, (pi) => pi.item?.category?.isUtilityCutout === true || pi.item?.sku === 'UTILITY-CUTOUT') > 0;
			if (!panel.isUtilityPanel && !hasUtilityCutout) return;
			const pageNumber = getPageForPanel(pages, panel);
			if (pageNumber === null) return;
			// "Front utility panel" never appears in production PDFs (0 of 9,126)
			// so we only label back / left-side / right-side utility panels.
			// Use the panel's own label so corner islands keep their compound orientation ("back left").
			const label = (panel.label ?? '').toLowerCase();
			let where: string | null = null;
			if (label.includes('back')) {
				where = label.includes('left') ? 'Back left utility panel'
					: label.includes('right') ? 'Back right utility panel'
						: 'Back utility panel';
			}
			else if (panel.isLeftPanel) where = 'Left-side utility panel';
			else if (panel.isRightPanel) where = 'Right-side utility panel';
			if (!where) return;

			const subject = islandLabel ? `${where} on${islandLabel}` : `${where} on ${islandDesc}`.trim();
			changes.push({
				pageRef: `Pg. ${pageNumber}`,
				description: subject,
				sortKey: pageNumber,
				count: 1
			});
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: custom / flat trim pieces                                       */
/* -------------------------------------------------------------------------- */

const detectCustomTrimPieces = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const changes: Array<NonStandardChange> = [];

	kitchen.design.islands.children.forEach((island) => {
		const trims = island.panelTrims?.children ?? [];
		const flatCount = trims.filter((t) => t.trimType === 'straight').length;
		if (flatCount === 0) return;

		const islandPages = getIslandRangePages(pages, island.id);
		if (islandPages.length === 0) return;
		const noun = formatQuantityPrefix(flatCount, 'Flat trim piece');
		changes.push({
			pageRef: `Pg. ${islandPages[0]}`,
			description: noun,
			sortKey: islandPages[0],
			count: flatCount
		});
	});

	return changes;
};

/* -------------------------------------------------------------------------- */
/*  Detector: custom backsplashes                                             */
/* -------------------------------------------------------------------------- */

const detectCustomBacksplashes = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;

	let total = 0;
	kitchen.design.islands.children.forEach((island) => {
		const countertops = kitchen.design.countertops.filterByIslandId(island.id);
		countertops.forEach((ct) => {
			total += ct.backsplashes?.children?.length ?? 0;
		});
	});
	if (total === 0) return [];

	const bsPage = pages.find((p) => p.pageType === 'backsplash')?.pageNumber ?? null;
	if (bsPage === null) return [];

	return [{
		pageRef: `Pg. ${bsPage}`,
		description: 'Backsplash',
		sortKey: bsPage,
		count: 1
	}];
};

/* -------------------------------------------------------------------------- */
/*  Detector: no-countertop reference                                         */
/* -------------------------------------------------------------------------- */

const detectNonRtaCountertop = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	const materialKey = kitchen.material?.countertop?.key ?? '';
	if (materialKey !== 'none') return [];

	// Find all countertop pages and reference them once as a combined entry.
	const ctPages = pages.filter((p) => p.pageType === 'countertop').map((p) => p.pageNumber).sort((a, b) => a - b);
	if (ctPages.length === 0) return [];
	const pageRef = ctPages.length === 1
		? `Pg. ${ctPages[0]}`
		: `Pgs. ${ctPages.join(', ')}`;
	return [{
		pageRef,
		description: 'Countertop measurements per RTA specs for customer/contractor reference',
		sortKey: ctPages[0],
		count: 1
	}];
};

/* -------------------------------------------------------------------------- */
/*  Detector: leveling legs                                                   */
/* -------------------------------------------------------------------------- */

const PANEL_PAGE_TYPES = ['front-panel', 'side-panels', 'back-panel', 'combined-front-back'];

// Collapses [4,5,6,9] into '4-6, 9' so a whole-kitchen note reads as a span rather than a list.
const formatPageRanges = (pageNumbers: Array<number>): string => {
	const ranges: Array<string> = [];
	let start = pageNumbers[0];
	let previous = pageNumbers[0];

	for (let i = 1; i <= pageNumbers.length; i++) {
		const current = pageNumbers[i];
		if (current === previous + 1) {
			previous = current;
			continue;
		}
		ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
		start = current;
		previous = current;
	}

	return ranges.join(', ');
};

const detectLevelingLegs = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const { kitchen, pages } = params;
	if (!hasLevelingLegs({ kitchen })) return [];

	const panelPages = pages
		.filter((p) => PANEL_PAGE_TYPES.includes(p.pageType))
		.map((p) => p.pageNumber)
		.sort((a, b) => a - b);
	if (panelPages.length === 0) return [];

	return [{
		pageRef: panelPages.length === 1 ? `Pg. ${panelPages[0]}` : `Pgs. ${formatPageRanges(panelPages)}`,
		description: 'Leveling legs added to the bottom of the panels',
		sortKey: panelPages[0],
		count: 1
	}];
};

/* -------------------------------------------------------------------------- */
/*  Public API                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Inspects the kitchen and returns every "Non-Standard Changes" entry the BP
 * overview should list — auto-derived from the design rather than freeform
 * notes. Based on a scan of 9,126 production PDFs, the following categories
 * are detected:
 *
 *   - Outlet cutouts on front / side / back panels
 *   - Pop-up outlets on countertop
 *   - Gas line cutouts on countertop or panel
 *   - Umbrella cutouts on countertop
 *   - Wood storage cutouts (the WOOD / LOG-INSERT catalog sku) on a panel
 *   - Generic custom cutouts (hand-drawn override items) on countertop or panel
 *   - Removed overhangs ("No <sides> overhang") — only when the removal is NOT
 *     caused by another island (island-butting removals are standard changes);
 *     enlarged overhangs print their value
 *   - Non-standard base height
 *   - Non-standard base depth (any depth other than 28") — covers the legacy
 *     "Nft Bar Island to be grill depth" / "to be bar depth" case, which was
 *     phased out in 2025 PDFs in favor of the standard base-depth note.
 *   - Utility panels (back / left / right / front)
 *   - Flat trim pieces (panel trims with type 'straight')
 *   - Custom backsplashes (any backsplash on a countertop)
 *   - No-countertop reference note
 *   - Leveling legs (the Leveling-Legs accessory on the kitchen)
 *
 * Results are sorted by page number (low → high) so Page 5 appears before
 * Page 12. The same note is never generated twice for the same
 * island+direction pair.
 */
// Helper: run a detector but swallow runtime errors so one bad detector does
// not take down the whole Kitchen Overview page. Errors are logged once to
// the console for debugging.
const safeRun = (
	name: string,
	detector: (params: GetNonStandardChangesParams) => Array<NonStandardChange>,
	params: GetNonStandardChangesParams
): Array<NonStandardChange> => {
	try {
		return detector(params) ?? [];
	} catch (err) {
		console.error(`[getNonStandardChanges] ${name} threw:`, err);
		return [];
	}
};

export const getNonStandardChanges = (params: GetNonStandardChangesParams): Array<NonStandardChange> => {
	const all = [
		...safeRun('detectOutletCutouts', detectOutletCutouts, params),
		...safeRun('detectPopUpOutlets', detectPopUpOutlets, params),
		...safeRun('detectGasLineCutouts', detectGasLineCutouts, params),
		...safeRun('detectUmbrellaCutouts', detectUmbrellaCutouts, params),
		...safeRun('detectWoodStorageCutouts', detectWoodStorageCutouts, params),
		...safeRun('detectCustomCutouts', detectCustomCutouts, params),
		...safeRun('detectReducedOverhangs', detectReducedOverhangs, params),
		...safeRun('detectNonStandardBaseHeight', detectNonStandardBaseHeight, params),
		...safeRun('detectNonStandardBaseDepth', detectNonStandardBaseDepth, params),
		...safeRun('detectUtilityPanels', detectUtilityPanels, params),
		...safeRun('detectCustomTrimPieces', detectCustomTrimPieces, params),
		...safeRun('detectCustomBacksplashes', detectCustomBacksplashes, params),
		...safeRun('detectNonRtaCountertop', detectNonRtaCountertop, params),
		...safeRun('detectLevelingLegs', detectLevelingLegs, params)
	];

	// De-duplicate by (pageRef + description) pair — some detectors may
	// overlap on edge cases (e.g., gas line and umbrella on same panel
	// triggering both custom cutout and dedicated entries).
	const seen = new Set<string>();
	const deduped: Array<NonStandardChange> = [];
	for (const change of all) {
		const key = `${change.pageRef}|${change.description}`;
		if (seen.has(key)) continue;
		seen.add(key);
		deduped.push(change);
	}

	return deduped.sort((a, b) => a.sortKey - b.sortKey);
};
